import { useState, useEffect } from "react"
import styled from "styled-components"
import ErrorableInputWrapper from "../common/ErrorableInputWrapper"
import EmailAuthInput from "./EmailAuthInput"
import VerificationCodeInput from "./VerificationCodeInput"
import MainButton from "../../atoms/MainButton"
import { validateEmail } from "../../../utils/userValidation"
import { sendEmailAuth, sendResetPasswordEmail, verifyCode } from "../../../api/auth"

const Wrapper = styled.form`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px; 
`
const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 600px; // Wrapper 최대 폭과 맞춤
  padding: 0 20px; // 좌우 여유
`

const EmailAuthStep = ({emailAuth, setEmailAuth, setUserInfo, onNext, mode = 'signup'}) => {
  
  const [errors, setErrors] = useState({email: '', code: ''}) // 에러 메시지 상태관리
  const [remainingTime, setRemainingTime] = useState(180); // 타이머 시간
  const [isTimerRunning, setIsTimerRunning] = useState(false); // 타이머 실행 여부
  const [isEmailSent, setIsEmailSent] = useState(false); // 이메일 인증 버튼을 한번이라도 눌렀는지 확인하기 위한 변수
  
  // 회원가입에서 쓰이는 컴포넌트인지
  const isSignup = mode === 'signup';
  const buttonLabel = isSignup ? '다음' : '확인'

  // 버튼 텍스트
  const getButtonLabel = () => isEmailSent ? '재인증' : '인증받기'

  useEffect(() => {
    if (!isTimerRunning) return; // 타이머가 실행되지 않으면 setInterval 실행 x

    const timer = setInterval(() => {
      setRemainingTime((prevTime) => { // setRemainingTime: 1초씩 감소
        if (prevTime <= 1) { // 이전 값이 1보다 작거나 같으면 코드 중단(클린업 함수 실행)
          clearInterval(timer);
          setIsTimerRunning(false);
          return 0; // remainingTime을 0으로 업데이트
        }
        return prevTime - 1 // if 조건문이 false면 1초씩 감소
      })
    }, 1000); // 1초 간격으로 setRemainingTime 함수 실행
    
    return () => clearInterval(timer); // 메모리 누수 방지용 클린업 함수

  }, [isTimerRunning]);

  // 시간을 분과 초로 변환하는 함수 정의
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // input 이벤트 핸들러
  const handleChange = (field) => (e) => {
    setEmailAuth(prev => ({ ...prev, [field]: e.target.value }));

    // 입력을 시작하면 해당 필드의 에러 메시지 초기화
    if (errors[field]) {
     setErrors(prev => ({ ...prev, [field]: '' }));
   }
  }

  // focus를 벗어날 때 유효성 검증 후 에러 메시지 표시
  const handleBlur = (field) => {
    let error = '';
    if (field === 'email') {
      if (!emailAuth.email.trim()) error = "이메일을 입력해주세요.";
      else if (!validateEmail(emailAuth.email)) error = "이메일 형식이 올바르지 않습니다.";
    }
    if (field === 'code') {
      if (!emailAuth.code.trim()) error = "인증번호를 입력해주세요.";
    }
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // 이메일 인증 버튼 클릭 핸들러
  const handleSendEmail = async(e) => {
    e.preventDefault();
    // UI 즉시 바뀜
    setRemainingTime(180);
    setIsTimerRunning(true);
    setIsEmailSent(true);
    try {
      if (isSignup) {
        await sendEmailAuth(emailAuth.email);
        // console.log(res)
      } else {
        await sendResetPasswordEmail(emailAuth.email);
        // console.log(res)
      }
    } catch (err) {
      // 실패 시 롤백
      setIsTimerRunning(false);
      setIsEmailSent(false);
      
      if (err.response.status === 403) {
        setErrors(prevErrors => ({
          ...prevErrors,
          email: isSignup
            ? '이미 가입한 계정이 있어요.'
            : '입력하신 이메일로 가입된 계정이 없어요.'
        }));
        return;
      }
      // console.log(err);
      console.error(err);
    }
  }

  // 다음 버튼 클릭 핸들러(인증 코드 인증 단계)
  const handleVerifyCode = async(e) => {
    e?.preventDefault();
    if (!isBtnValid) return;

    try {
      await verifyCode({ email: emailAuth.email, tempCode: emailAuth.code })
      
      if (isSignup) {
        setUserInfo(prev => ({ ...prev, email: emailAuth.email }));
        sessionStorage.setItem('emailVerified', 'true')
      }
      onNext();
    } catch (err) {
      // 인증코드가 틀린 경우
      if (err.response.status === 401) {
        setErrors(prevErrors => ({
          ...prevErrors,
          code: '인증코드가 틀렸어요.'
        }));
      }
      console.log(err)
    }
  }

  // const isBtnValid = emailAuth.email.trim() !== '' && !errors.email && emailAuth.code.trim() !== '' && !errors.code;                    
  const isBtnValid = validateEmail(emailAuth.email) && emailAuth.code.length === 6;

  return (
    <Wrapper onSubmit={handleVerifyCode}>
      <ErrorableInputWrapper $error={!!errors.email} message={errors.email}>
        <EmailAuthInput
          name="email"
          autoComplete="email"
          type="email"
          placeholder="example@example.com"
          value={emailAuth.email} 
          onChange={handleChange('email')}
          onBlur={()=>handleBlur('email')}
          $error={!!errors.email}
          buttonLabel={getButtonLabel()}
          onSendEmail={handleSendEmail}
        />
      </ErrorableInputWrapper>
      <ErrorableInputWrapper $error={!!errors.code} message={errors.code}>
        <VerificationCodeInput
          name="code"
          autoComplete="off"
          placeholder="인증코드 6자리"
          value={emailAuth.code} 
          onChange={handleChange('code')}
          onBlur={()=>handleBlur('code')}
          $error={!!errors.code}
          timer={formatTime(remainingTime)}
          isTimerRunning={isTimerRunning}
          />
      </ErrorableInputWrapper>
      <ButtonWrapper>
        <MainButton
          $variant="primary"
          title={buttonLabel}
          disabled={!isBtnValid}
          type="submit"/>
      </ButtonWrapper>
    </Wrapper>
  )
}

export default EmailAuthStep
